import React from "react";

import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "semantic-ui-react";

import "./TableEmployee.scss";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import {
  BTN_ACTS,
  TrainingAttendanceType,
  UpdateStatusParamsType,
} from "./sequence/TrainingManagement";

import { COURSE_TYPE_OPTIONS, getCourseApproveStatus } from "./HRMInterface";

import { formatDate } from "react-lib/utils/dateUtils";

type TableTrainingManagementProps = {
  data?: TrainingAttendanceType[];
  buttonLoadCheck?: Record<string, any>;
  onUpdateStatus: (data: UpdateStatusParamsType) => void;
};

const TableTrainingManagement = (props: TableTrainingManagementProps) => {
  const sortedItems = (props.data ?? []).sort((a, b) => {
    if (a.employee_code !== b.employee_code) {
      return (a.employee_code || "").localeCompare(b.employee_code || "");
    }

    if (a.course_id !== b.course_id) {
      return (a.course_id || "").localeCompare(b.course_id || "");
    }

    const dateA = moment(a.attendance_datetime);
    const dateB = moment(b.attendance_datetime);

    return dateA.diff(dateB); // Ascending order
  });

  return (
    <div className="table-tm">
      <Table celled compact structured>
        <TableHeader>
          <TableRow style={{ backgroundColor: "#4ecdc4" }}>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={2}
            >
              Employee ID
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={3}
            >
              Employee Name
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={4}
            >
              Course
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={2}
            >
              Course Type
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={2}
            >
              Participant date
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={2}
            >
              Status
            </TableHeaderCell>
            <TableHeaderCell
              style={{ color: "white", fontWeight: "bold" }}
              textAlign="center"
              width={2}
            >
              Action
            </TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody>
          {sortedItems.length === 0 ? (
            <TableRow>
              <TableCell style={{ color: "grey" }} colSpan={7} textAlign="center">
                No rows found
              </TableCell>
            </TableRow>
          ) : null}

          {sortedItems.map((item, index: number) => {
            // Group rows by employee ID
            const isFirstRowForEmployee =
              index === 0 || sortedItems[index - 1].employee_code !== item.employee_code;

            const rowsForThisEmployee = sortedItems.filter(
              (d) => d.employee_code === item.employee_code
            ).length;

            // Group rows by course ID within the same employee
            const isFirstRowForCourse =
              index === 0 ||
              sortedItems[index - 1].employee_code !== item.employee_code ||
              sortedItems[index - 1].course_id !== item.course_id;

            const rowsForThisCourse = sortedItems.filter(
              (d) => d.employee_code === item.employee_code && d.course_id === item.course_id
            ).length;

            const courseType = COURSE_TYPE_OPTIONS.find(
              (option) => option.value === item.course_training_type
            );
            const status = getCourseApproveStatus(item.status);

            const disabledAction = moment(item.attendance_datetime).isAfter(moment(), "day");

            return (
              <TableRow key={`${item.employee_code}-${item.course_id}-${index}`}>
                {/* Employee ID - merge for all courses of this employee */}
                {isFirstRowForEmployee && (
                  <TableCell rowSpan={rowsForThisEmployee} textAlign="center" verticalAlign="top">
                    {item.employee_code}
                  </TableCell>
                )}

                {/* Employee Name - merge for all courses of this employee */}
                {isFirstRowForEmployee && (
                  <TableCell rowSpan={rowsForThisEmployee} verticalAlign="top">
                    {item.employee_name}
                  </TableCell>
                )}

                {/* Course - merge for all days of this course */}
                {isFirstRowForCourse && (
                  <TableCell rowSpan={rowsForThisCourse} verticalAlign="top">
                    [{item.course_id}] {item.course_name}
                    {[
                      ["Course start date", item.course_start_date],
                      ["Course end date", item.course_end_date],
                    ].map(([label, date]) => (
                      <div key={label} style={{ display: "flex" }}>
                        <div style={{ width: "115px" }}>{label}</div>
                        <div style={{ marginRight: "10px" }}>:</div>
                        <div>{formatDate(moment(date))}</div>
                      </div>
                    ))}
                  </TableCell>
                )}

                {/* Course Type - merge for all days of this course */}
                {isFirstRowForCourse && (
                  <TableCell rowSpan={rowsForThisCourse} textAlign="center" verticalAlign="top">
                    {courseType?.text || ""}
                  </TableCell>
                )}

                {/* Date - show individual date for each day */}
                <TableCell textAlign="center" verticalAlign="top">
                  <div>{item.attendance_th_date}</div>
                </TableCell>

                {/* Status - individual status for each day */}
                <TableCell textAlign="center" verticalAlign="top">
                  <div
                    style={{
                      alignItems: "center",
                      display: "flex",
                      gap: "5px",
                      justifyContent: "center",
                    }}
                  >
                    <span
                      style={{
                        marginLeft: item.status === "APPROVE" ? "0.35rem" : "",
                        textTransform: "capitalize",
                      }}
                    >
                      {status?.name.toLowerCase()}
                    </span>
                    {!!status && (
                      <Icon style={{ marginTop: "-5px" }} color={status.color} name={status.icon} />
                    )}
                  </div>
                </TableCell>

                {/* Action - individual action buttons for each day */}
                <TableCell textAlign="center" verticalAlign="top">
                  <div style={{ display: "flex", gap: "3px" }}>
                    {item.status === "UNAPPROVE" && (
                      <>
                        <ButtonLoadCheck
                          style={{ fontSize: "0.85rem", minWidth: "4.5rem", padding: "6px 8px" }}
                          color="green"
                          disabled={disabledAction}
                          paramKey={`${BTN_ACTS.UPDATE_STATUS}_APPROVE_${item.id}`}
                          size="mini"
                          title="Attend"
                          buttonLoadCheck={
                            props.buttonLoadCheck?.[`${BTN_ACTS.UPDATE_STATUS}_APPROVE_${item.id}`]
                          }
                          onClick={() => {
                            props.onUpdateStatus({ id: item.id, status: "APPROVE" });
                          }}
                        />
                        <ButtonLoadCheck
                          style={{ fontSize: "0.85rem", minWidth: "4.5rem", padding: "6px 8px" }}
                          color="yellow"
                          disabled={disabledAction}
                          paramKey={`${BTN_ACTS.UPDATE_STATUS}_ABSTAIN_${item.id}`}
                          size="mini"
                          title="Abstain"
                          buttonLoadCheck={
                            props.buttonLoadCheck?.[`${BTN_ACTS.UPDATE_STATUS}_ABSTAIN_${item.id}`]
                          }
                          onClick={() => {
                            props.onUpdateStatus({ id: item.id, status: "ABSTAIN" });
                          }}
                        />
                      </>
                    )}
                    {["ABSTAIN", "APPROVE"].includes(item.status) && (
                      <ButtonLoadCheck
                        style={{ fontSize: "0.85rem", minWidth: "4.5rem", padding: "6px 8px" }}
                        color="red"
                        disabled={disabledAction}
                        paramKey={`${BTN_ACTS.UPDATE_STATUS}_UNAPPROVE_${item.id}`}
                        size="mini"
                        title="Unapproved"
                        buttonLoadCheck={
                          props.buttonLoadCheck?.[`${BTN_ACTS.UPDATE_STATUS}_UNAPPROVE_${item.id}`]
                        }
                        onClick={() => {
                          props.onUpdateStatus({ id: item.id, status: "UNAPPROVE" });
                        }}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

TableTrainingManagement.displayName = "TableTrainingManagement";

export default React.memo(TableTrainingManagement);
